import { action, computed, makeObservable, observable, toJS } from 'mobx';
import { getRandomNumber } from 'src/shared/utils/get-random-number';

import { isSectionField } from './rig-operations-service';
import { TRawRigOperationSection } from './types';

export type TSectionData = {
  startDate: number | null;
  endDate: number | null;
  duration: number | null;
  sectionTypeId: number | null;
  passingValue: number | null;
  passingValueMin: number | null;
  passingValueMax: number | null;
  index: number;
};

type TErrors = Partial<Record<keyof TSectionData, string>>;

export const SECTION_DATA_FIELDS: string[] & (keyof TSectionData)[] = [
  'startDate',
  'endDate',
  'duration',
  'sectionTypeId',
  'passingValue',
  'passingValueMin',
  'passingValueMax',
  'index',
];

export class RigOperationSection {
  readonly entityId = getRandomNumber();
  readonly initialRawSection?: Partial<TRawRigOperationSection>;

  @observable private _errors: TErrors = {};
  @observable private _data: TSectionData;

  constructor(index: number, initialValues?: Partial<TRawRigOperationSection>) {
    this._data = {
      startDate: initialValues?.startDate ?? null,
      endDate: initialValues?.endDate ?? null,
      duration: initialValues?.duration ?? null,
      sectionTypeId: initialValues?.sectionTypeId ?? null,
      passingValue: initialValues?.passingValue ?? null,
      passingValueMin: initialValues?.passingValueMin ?? null,
      passingValueMax: initialValues?.passingValueMax ?? null,
      index,
    };
    this.initialRawSection = initialValues;

    makeObservable(this);
  }

  get data(): TSectionData {
    return toJS(this._data);
  }

  @computed
  get errors(): TErrors {
    return toJS(this._errors);
  }

  @action.bound
  clearErrors(): void {
    this._errors = {};
  }

  @action.bound
  addError(fieldId: keyof TSectionData, errorText: string): void {
    this._errors = { ...this._errors, [fieldId]: errorText };
  }

  @action.bound
  removeError(fieldId: keyof TSectionData): void {
    const errors: TErrors = {};

    for (const key in this._errors) {
      if (key === fieldId || !isSectionField(key)) {
        continue;
      }

      errors[key] = this._errors[key];
    }

    this._errors = errors;
  }

  @computed
  get startDate(): number | null {
    return this._data.startDate;
  }

  @action.bound
  setStartDate(value: number | null): void {
    this._data = { ...this._data, startDate: value };
  }

  @computed
  get endDate(): number | null {
    return this._data.endDate;
  }

  @action.bound
  setEndDate(value: number | null): void {
    this._data = { ...this._data, endDate: value };
  }

  @computed
  get duration(): number | null {
    return this._data.duration;
  }

  @action.bound
  setDuration(value: number | null): void {
    this._data = { ...this._data, duration: value };
  }

  @computed
  get sectionTypeId(): number | null {
    return this._data.sectionTypeId;
  }

  @action.bound
  setSectionTypeId(value: number | null): void {
    this._data = { ...this._data, sectionTypeId: value };
  }

  @computed
  get passingValue(): number | null {
    return this._data.passingValue;
  }

  @action.bound
  setPassingValue(value: number | null): void {
    this._data = { ...this._data, passingValue: value };
  }

  @computed
  get passingValueMin(): number | null {
    return this._data.passingValueMin;
  }

  @action.bound
  setPassingValueMin(value: number | null): void {
    this._data = { ...this._data, passingValueMin: value };
  }

  @computed
  get passingValueMax(): number | null {
    return this._data.passingValueMax;
  }

  @action.bound
  setPassingValueMax(value: number | null): void {
    this._data = { ...this._data, passingValueMax: value };
  }
}
